import type { SettingsTemplateType } from '@/shared/generated'
import type { Active } from '@dnd-kit/core'

import React, { useState } from 'react'
import { useDndMonitor, useDroppable } from '@dnd-kit/core'

import { cn } from '@/shared/lib/utils'

type TemplateBuilderSectionProps = {
	id: string
	children: React.ReactNode
	active?: boolean
	error?: boolean
	empty?: boolean
	accepts: SettingsTemplateType[]
}

export default function TemplateBuilderSection({
	id,
	children,
	active,
	error,
	empty,
	accepts,
}: TemplateBuilderSectionProps) {
	const [draggedItem, setDraggedItem] = useState<Active | null>(null)
	useDndMonitor({
		onDragStart(event) {
			setDraggedItem(event.active)
		},
		onDragCancel() {
			setDraggedItem(null)
		},
		onDragEnd() {
			setDraggedItem(null)
		},
	})

	const { isOver, setNodeRef } = useDroppable({
		id: id,
		disabled: !accepts.includes(
			draggedItem?.data?.current?.block?.template_type
		),
		data: {
			accepts,
		},
	})

	return (
		<div
			ref={setNodeRef}
			data-testid={`builder-section-${id}-droppable`}
			className={cn(
				'flex h-full flex-col rounded-lg border-2 border-dashed bg-secondary p-2',
				{
					'border-primary': isOver,
					'border-primary-300': active && !isOver,
					'cursor-pointer border-2 border-dashed bg-input/20 hover:bg-accent dark:bg-input/30':
						empty,
					'border-destructive hover:border-destructive/75': error,
				}
			)}
		>
			{children}
		</div>
	)
}
